import React, { useState, useEffect } from 'react';
import { Plus, Edit, X, Calendar, Percent, Tag } from 'lucide-react';
import { Promotion, Product } from '../../types';

export const AdminPromotions: React.FC = () => {
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [editingPromo, setEditingPromo] = useState<Partial<Promotion> | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const fetchPromos = async () => {
    try {
      const res = await fetch('/api/admin/promotions');
      if (res.ok) {
        const data = await res.json() as any;
        setPromotions(data.promotions || []);
      }
      const pRes = await fetch('/api/admin/products');
      if (pRes.ok) {
        const pData = await pRes.json() as any;
        setProducts(pData.products || []);
      }
    } catch {}
  };

  useEffect(() => {
    fetchPromos();
  }, []);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingPromo) return;

    try {
      const res = await fetch('/api/admin/promotions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editingPromo)
      });

      if (res.ok) {
        setMessage('Promotion enregistrée avec succès.');
        setEditingPromo(null);
        fetchPromos();
        setTimeout(() => setMessage(null), 3000);
      }
    } catch {
      alert('Erreur lors de l’enregistrement de la promotion.');
    }
  };

  // Preview final price calculation
  const targetProduct = products.find(p => p.id === editingPromo?.product_id) || products[0];
  let previewChf = targetProduct?.price_chf || 24.90;
  let previewEur = targetProduct?.price_eur || 24.90;

  if (editingPromo) {
    if (editingPromo.discount_type === 'percentage') {
      const pctChf = editingPromo.discount_value_chf || 0;
      const pctEur = editingPromo.discount_value_eur || 0;
      previewChf = Math.max(0, previewChf - Math.round(previewChf * (pctChf / 100) * 100) / 100);
      previewEur = Math.max(0, previewEur - Math.round(previewEur * (pctEur / 100) * 100) / 100);
    } else {
      previewChf = Math.max(0, previewChf - (editingPromo.discount_value_chf || 0));
      previewEur = Math.max(0, previewEur - (editingPromo.discount_value_eur || 0));
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Gestion des Promotions</h2>
          <p className="text-xs text-gray-500">
            Offres temporaires affichées sur la boutique uniquement pendant leur période active
          </p>
        </div>
        <button
          onClick={() => {
            const now = new Date();
            const nextWeek = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
            setEditingPromo({
              name: 'Offre Découverte',
              discount_type: 'percentage',
              discount_value_chf: 10,
              discount_value_eur: 10,
              start_at: now.toISOString().slice(0, 16),
              end_at: nextWeek.toISOString().slice(0, 16),
              is_active: 1,
              product_id: products[0]?.id || null
            });
          }}
          className="inline-flex items-center gap-1.5 px-3 py-2 bg-neutral-900 text-white rounded-xl text-xs font-bold hover:bg-pink-600 transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>Créer une promotion</span>
        </button>
      </div>

      {message && (
        <div className="p-3 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-xl text-xs font-semibold">
          {message}
        </div>
      )}

      {/* Edit modal */}
      {editingPromo && (
        <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-4">
          <div className="flex justify-between items-center border-b pb-3">
            <h3 className="text-sm font-bold text-gray-900">
              {editingPromo.id ? 'Modifier la promotion' : 'Nouvelle promotion'}
            </h3>
            <button onClick={() => setEditingPromo(null)} className="text-gray-400 hover:text-gray-700">
              <X className="w-4 h-4" />
            </button>
          </div>

          <form onSubmit={handleSave} className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
            <div>
              <label className="block font-semibold mb-1">Nom interne de l’offre</label>
              <input
                type="text"
                required
                value={editingPromo.name || ''}
                onChange={e => setEditingPromo({ ...editingPromo, name: e.target.value })}
                className="w-full p-2 border rounded-lg"
                placeholder="Ex: Solde d'été"
              />
            </div>

            <div>
              <label className="block font-semibold mb-1">Produit cible</label>
              <select
                value={editingPromo.product_id || ''}
                onChange={e => setEditingPromo({ ...editingPromo, product_id: e.target.value || null })}
                className="w-full p-2 border rounded-lg bg-white"
              >
                <option value="">Tous les produits (Global)</option>
                {products.map(p => (
                  <option key={p.id} value={p.id}>{p.name_fr} ({p.sku})</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block font-semibold mb-1">Type de réduction</label>
              <select
                value={editingPromo.discount_type || 'percentage'}
                onChange={e =>
                  setEditingPromo({
                    ...editingPromo,
                    discount_type: e.target.value as 'percentage' | 'fixed'
                  })
                }
                className="w-full p-2 border rounded-lg bg-white"
              >
                <option value="percentage">Pourcentage (%)</option>
                <option value="fixed">Montant fixe déduit</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block font-semibold mb-1">Valeur CHF</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={editingPromo.discount_value_chf ?? 0}
                  onChange={e =>
                    setEditingPromo({ ...editingPromo, discount_value_chf: parseFloat(e.target.value) })
                  }
                  className="w-full p-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block font-semibold mb-1">Valeur EUR</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={editingPromo.discount_value_eur ?? 0}
                  onChange={e =>
                    setEditingPromo({ ...editingPromo, discount_value_eur: parseFloat(e.target.value) })
                  }
                  className="w-full p-2 border rounded-lg"
                />
              </div>
            </div>

            <div>
              <label className="block font-semibold mb-1">Date de début (ISO)</label>
              <input
                type="datetime-local"
                required
                value={(editingPromo.start_at || '').slice(0, 16)}
                onChange={e =>
                  setEditingPromo({ ...editingPromo, start_at: new Date(e.target.value).toISOString() })
                }
                className="w-full p-2 border rounded-lg"
              />
            </div>

            <div>
              <label className="block font-semibold mb-1">Date de fin (ISO)</label>
              <input
                type="datetime-local"
                required
                value={(editingPromo.end_at || '').slice(0, 16)}
                onChange={e =>
                  setEditingPromo({ ...editingPromo, end_at: new Date(e.target.value).toISOString() })
                }
                className="w-full p-2 border rounded-lg"
              />
            </div>

            <div className="sm:col-span-2">
              <label className="flex items-center gap-2 cursor-pointer pt-1">
                <input
                  type="checkbox"
                  checked={editingPromo.is_active === 1}
                  onChange={e => setEditingPromo({ ...editingPromo, is_active: e.target.checked ? 1 : 0 })}
                  className="rounded text-pink-600"
                />
                <span className="font-semibold">Promotion activée</span>
              </label>
            </div>

            {/* Price preview container */}
            <div className="sm:col-span-2 bg-pink-50/60 border border-pink-200/60 p-4 rounded-xl flex items-center justify-between">
              <div>
                <span className="font-bold text-pink-900 block">Aperçu du prix final pour le client :</span>
                <span className="text-[11px] text-pink-700">Calculé sur la base de {targetProduct?.name_fr || 'Produit'}</span>
              </div>
              <div className="text-right font-mono font-bold text-sm text-pink-950">
                <span>{previewChf.toFixed(2)} CHF</span> • <span>{previewEur.toFixed(2)} EUR</span>
              </div>
            </div>

            <div className="sm:col-span-2 pt-2 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setEditingPromo(null)}
                className="px-4 py-2 border rounded-xl font-bold"
              >
                Annuler
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-neutral-900 text-white rounded-xl font-bold hover:bg-pink-600 transition-colors"
              >
                Enregistrer
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Promotions Table */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <table className="w-full text-left text-xs">
          <thead className="bg-gray-50 border-b text-gray-500 font-semibold uppercase tracking-wider">
            <tr>
              <th className="p-4">Nom</th>
              <th className="p-4">Type & Valeur</th>
              <th className="p-4">Période</th>
              <th className="p-4">Statut</th>
              <th className="p-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {promotions.length === 0 ? (
              <tr>
                <td colSpan={5} className="p-8 text-center text-gray-400">
                  Aucune promotion configurée.
                </td>
              </tr>
            ) : (
              promotions.map(p => {
                const now = new Date().toISOString();
                const isCurrentlyActive = p.is_active === 1 && p.start_at <= now && p.end_at >= now;
                return (
                  <tr key={p.id} className="hover:bg-gray-50/50">
                    <td className="p-4 font-bold text-gray-900">{p.name}</td>
                    <td className="p-4 font-mono">
                      {p.discount_type === 'percentage'
                        ? `-${p.discount_value_chf}%`
                        : `-${p.discount_value_chf.toFixed(2)} CHF / -${p.discount_value_eur.toFixed(2)} EUR`}
                    </td>
                    <td className="p-4 text-[11px] text-gray-600">
                      {new Date(p.start_at).toLocaleDateString()} → {new Date(p.end_at).toLocaleDateString()}
                    </td>
                    <td className="p-4">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-bold ${
                          isCurrentlyActive
                            ? 'bg-emerald-100 text-emerald-800'
                            : p.is_active === 1
                            ? 'bg-amber-100 text-amber-800'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {isCurrentlyActive ? 'En cours' : p.is_active === 1 ? 'Programmé / Échu' : 'Désactivé'}
                      </span>
                    </td>
                    <td className="p-4 text-right">
                      <button
                        onClick={() => setEditingPromo(p)}
                        className="p-1.5 text-gray-600 hover:text-pink-600 rounded-lg hover:bg-gray-100"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
